import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { AuthContext, type AuthContextValue } from "@/auth/auth-context";
import { clearStorage, getStorage, setStorage } from "@/lib/storage";
import { fetchMe, login as loginRequest } from "@/services/auth";
import type { PartnerProfile, PartnerSession } from "@/types/partner";

const SESSION_KEY = "session";

function readSession(): PartnerSession | null {
  return getStorage<PartnerSession>(SESSION_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<PartnerSession | null>(() => readSession());

  const persist = useCallback((next: PartnerSession | null) => {
    if (next) {
      setStorage(SESSION_KEY, next);
    } else {
      clearStorage();
    }
    setSession(next);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await loginRequest({ email: email.trim().toLowerCase(), password });
      if (result.status === "needs_password_reset") {
        return {
          status: "needs_password_reset" as const,
          email: result.email,
          message: result.message,
        };
      }
      persist(result.session);
      return { status: "authenticated" as const };
    },
    [persist],
  );

  const logout = useCallback(() => {
    persist(null);
  }, [persist]);

  const refreshProfile = useCallback(async () => {
    if (!session?.tokens?.access) return;
    const profile: PartnerProfile = await fetchMe();
    persist({ tokens: session.tokens, profile });
  }, [persist, session?.tokens]);

  useEffect(() => {
    const current = readSession();
    if (!current?.tokens?.access) return;

    let cancelled = false;
    void fetchMe()
      .then((profile) => {
        if (!cancelled) {
          persist({ tokens: current.tokens, profile });
        }
      })
      .catch(() => {
        // 401 interceptor clears storage / redirects when appropriate.
      });
    return () => {
      cancelled = true;
    };
  }, [persist]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: Boolean(session?.tokens?.access),
      login,
      logout,
      refreshProfile,
    }),
    [session, login, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
